import { ConversationType } from "../constants/chat.constant";

/**
 * ChatUtils class provides static utility methods for chat-related operations.
 * Includes URL conversion, mobile detection, conversation management, and message utilities.
 * 
 * @class
 * @example
 * ```tsx
 * const conversationType = ChatUtils.urlTypeToEnum('chop-gpt');
 * const isMobile = ChatUtils.isMobile();
 * const url = ChatUtils.generateConversationUrl(ConversationType.CHOP_GPT, 'session-id');
 * ```
 */
export class ChatUtils {
  /**
   * Converts URL type string to ConversationType enum.
   * 
   * @param {string} urlType - URL type string (e.g., "chop-gpt", "policy-gpt")
   * @returns {ConversationType} ConversationType enum value, defaults to CHOP_GPT if invalid
   * 
   * @example
   * ```tsx
   * const type = ChatUtils.urlTypeToEnum('chop-gpt'); // Returns ConversationType.CHOP_GPT
   * ```
   */
  static urlTypeToEnum(urlType: string): ConversationType {
    const formatted = urlType.toUpperCase().replace(/-/g, "_");
    return Object.values(ConversationType).includes(
      formatted as ConversationType
    )
      ? (formatted as ConversationType)
      : ConversationType.CHOP_GPT;
  }

  /**
   * Checks if the current device is mobile based on user agent.
   * 
   * @returns {boolean} True if device is mobile
   * 
   * @example
   * ```tsx
   * if (ChatUtils.isMobile()) {
   *   // Mobile-specific logic
   * }
   * ```
   */
  static isMobile(): boolean {
    const userAgent =
      typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    return mobileRegex.test(userAgent);
  }

  /**
   * Generates a conversation URL from conversation type and session ID.
   * 
   * @param {ConversationType} conversationType - Type of conversation
   * @param {string} sessionId - Conversation session ID
   * @returns {string} Conversation URL path
   * 
   * @example
   * ```tsx
   * const url = ChatUtils.generateConversationUrl(ConversationType.CHOP_GPT, 'abc-123');
   * // Returns: '/chat/chop-gpt/abc-123'
   * ```
   */
  static generateConversationUrl(
    conversationType: ConversationType,
    sessionId: string
  ): string {
    const urlType = conversationType.toLowerCase().replace(/_/g, "-");
    return `/chat/${urlType}/${sessionId}`;
  }

  /**
   * Generates a base conversation URL from conversation type (without session ID).
   * 
   * @param {ConversationType} conversationType - Type of conversation
   * @returns {string} Base conversation URL path
   * 
   * @example
   * ```tsx
   * const url = ChatUtils.generateBaseConversationUrl(ConversationType.CHOP_GPT);
   * // Returns: '/chat/chop-gpt'
   * ```
   */
  static generateBaseConversationUrl(conversationType: ConversationType): string {
    const urlType = conversationType.toLowerCase().replace(/_/g, "-");
    return `/chat/${urlType}`;
  }

  /**
   * Sorts conversations by date (most recent first).
   * Uses updated_at if available, otherwise created_at.
   * 
   * @template T - Type extending object with updated_at and/or created_at
   * @param {T[]} conversations - Array of conversations to sort
   * @returns {T[]} Sorted array (new array, original unchanged)
   * 
   * @example
   * ```tsx
   * const sorted = ChatUtils.sortConversationsByDate(conversations);
   * ```
   */
  static sortConversationsByDate<T extends { updated_at?: string; created_at?: string }>(
    conversations: T[]
  ): T[] {
    return [...conversations].sort((a, b) => {
      const aDate = a.updated_at || a.created_at;
      const bDate = b.updated_at || b.created_at;

      if (aDate && bDate) {
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      }
      return 0;
    });
  }

  /**
   * Truncates text to specified length with ellipsis.
   * 
   * @param {string} text - Text to truncate
   * @param {number} [maxLength=50] - Maximum length before truncation
   * @returns {string} Truncated text with ellipsis if needed
   * 
   * @example
   * ```tsx
   * const short = ChatUtils.truncateText('Long text here', 10);
   * // Returns: 'Long text ...'
   * ```
   */
  static truncateText(text: string, maxLength: number = 50): string {
    return text.slice(0, maxLength) + (text.length > maxLength ? "..." : "");
  }

  /**
   * Generates a conversation title from message content by truncating.
   * 
   * @param {string} messageContent - Message content to generate title from
   * @returns {string} Truncated message content (max 50 chars)
   * 
   * @example
   * ```tsx
   * const title = ChatUtils.generateConversationTitle('What is the policy for...');
   * ```
   */
  static generateConversationTitle(messageContent: string): string {
    return this.truncateText(messageContent, 50);
  }

  /**
   * Validates if a message has valid (non-empty) content.
   * 
   * @param {{content?: string}} message - Message object to validate
   * @returns {boolean} True if message has valid content
   * 
   * @example
   * ```tsx
   * if (ChatUtils.isValidMessage(message)) {
   *   // Process message
   * }
   * ```
   */
  static isValidMessage(message: { content?: string }): boolean {
    return Boolean(message.content?.trim());
  }

  /**
   * Creates a new conversation object with default title.
   * 
   * @param {string} sessionId - Session ID for the conversation
   * @param {ConversationType} conversationType - Type of conversation
   * @param {string} [title="New Conversation"] - Conversation title
   * @returns {Object} New conversation object
   * 
   * @example
   * ```tsx
   * const conv = ChatUtils.createNewConversation('session-123', ConversationType.CHOP_GPT);
   * ```
   */
  static createNewConversation(
    sessionId: string,
    conversationType: ConversationType,
    title: string = "New Conversation"
  ) {
    return {
      session_id: sessionId,
      title,
      conversation_type: conversationType,
    };
  }

  /**
   * Creates an assistant message placeholder with empty content and reasoning.
   * 
   * @returns {Object} Assistant message placeholder object
   * 
   * @example
   * ```tsx
   * const placeholder = ChatUtils.createAssistantMessagePlaceholder();
   * // Returns: { role: 'assistant', content: '', reasoning: '' }
   * ```
   */
  static createAssistantMessagePlaceholder() {
    return {
      role: "assistant" as const,
      content: "",
      reasoning: "",
    };
  }

  /**
   * Creates a standardized error message for assistant responses.
   * 
   * @returns {{role: "assistant"; content: string}} Error message object
   * 
   * @example
   * ```tsx
   * const errorMsg = ChatUtils.createErrorMessage();
   * // Returns: { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }
   * ```
   */
  static createErrorMessage(): { role: "assistant"; content: string } {
    return {
      role: "assistant",
      content: "Sorry, an error occurred. Please try again.",
    };
  }
}
